import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { GalavailServiceEcommer } from "clientwsGalavail";
import { NgWebflowService } from "ngweflow";
import { SearchCountryField, CountryISO, TooltipLabel } from "ngx-intl-tel-input-angular7";
import { isNullOrUndefined } from "util";
import { CabinaItinerarioMenorWrapper } from "src/app/classes/cabina-itinerario-menor-wrapper";
import { PassengerType } from "src/app/enums/passenger-type.enum";
import { WebFlowAction } from "src/app/core/enums/web-flow-actions.enum";
import { CabinaItinerarioMenor } from "src/app/models/cabina-itinerario-menor";
import { Company } from "src/app/models/company";
import { MultiDeparture } from "src/app/models/multi-departure";
import { CabinService } from "src/app/services/cabin/cabin.service";
import { OnholdService } from "../onhold.service";

interface DepartureCabinData {
  departure: MultiDeparture;
  cabins: CabinaItinerarioMenor[];
  cabinWrappers: CabinaItinerarioMenorWrapper[];
  initialized: boolean;
}

interface FlatCabinOptionData {
  departureIndex: number;
  cabinIndex: number;
  wrapper: CabinaItinerarioMenorWrapper;
  label: string;
}

@Component({
  selector: "app-multi-onhold",
  templateUrl: "./multi-onhold.component.html",
  styleUrls: ["./multi-onhold.component.css"],
})
export class MultiOnholdComponent implements OnInit {
  @Input() dataCompany: Company;
  @Input() departures: MultiDeparture[] = [];
  @Input() empresa: string;
  @Input() keyCompany: string;

  public departureCabinList: DepartureCabinData[] = [];
  public allDepartureOptions: FlatCabinOptionData[] = [];
  // Una fila (FormGroup) por cada salida disponible; no hay botón "Add Departure".
  // Adults/Children inician en 0 y la salida se activa (pasa a formar parte del
  // bloqueo múltiple) cuando adults > 0 o children > 0; el basurero los resetea a 0.
  public cabinRows: FormGroup[] = [];
  public allLoaded = false;
  public formGroupOnHold: FormGroup;
  public token = "";
  public messageCode = "";
  public codeError = "";
  public codeValidationEntered = "";
  public hasEmailRequest = false;
  public isLoading = false;
  public isLoadingOnHold = false;
  public isVisibleDialog = false;
  public dialogObj: { title: string; message: string } = { title: "", message: "" };

  public SearchCountryField = SearchCountryField;
  public TooltipLabel = TooltipLabel;
  public CountryISO = CountryISO;

  constructor(
    private fb: FormBuilder,
    private cabinService: CabinService,
    private ngWebflowService: NgWebflowService,
    private galavailServiceEcommer: GalavailServiceEcommer,
    private onholdService: OnholdService,
    private router: Router
  ) {}

  ngOnInit() {
    this.departureCabinList = this.departures.map((departure) => ({
      departure,
      cabins: [],
      cabinWrappers: [],
      initialized: false,
    }));
    this.cabinRows = [];
    this.formGroupOnHold = this.fb.group({
      email: ["", [Validators.required, Validators.email]],
      reference: ["", [Validators.required]],
      remarks: [""],
      telephone: [""],
      agency: [""],
      itiObject: [null],
    });
    this.loadCabins();
  }

  private loadCabins(): void {
    this.departureCabinList.forEach((depData, index) => {
      this.galavailServiceEcommer
        .findCabins({
          adults: 1,
          children: 0,
          itinerary: depData.departure.code,
          code: this.dataCompany.code,
        })
        .subscribe(
          (cabinas: CabinaItinerarioMenor[]) => {
            depData.cabins = cabinas || [];
            depData.cabinWrappers = (cabinas || []).map(
              (cabina) => new CabinaItinerarioMenorWrapper(cabina)
            );
            depData.initialized = true;
            this.recomputeAllOptions();
          },
          (error) => {
            console.error("MultiOnholdComponent.loadCabins() error", error);
            depData.cabins = [];
            depData.cabinWrappers = [];
            depData.initialized = true;
            this.recomputeAllOptions();
          }
        );
    });
  }

  private recomputeAllOptions(): void {
    const options: FlatCabinOptionData[] = [];
    this.departureCabinList.forEach((depData, departureIndex) => {
      if (!depData.initialized) {
        return;
      }
      // Tour diario: la salida es un único valor (una sola fila en el select),
      // aunque findCabins devuelva varios spots/cabinas. Se toma la primera cabina
      // disponible (precio/acomodación) y se evita repetir la misma salida.
      if (depData.cabinWrappers.length === 0) {
        return;
      }
      const wrapper = depData.cabinWrappers[0];
      options.push({
        departureIndex,
        cabinIndex: 0,
        wrapper,
        label: this.buildOptionLabel(depData, wrapper),
      });
    });
    this.allDepartureOptions = options;
    this.rebuildCabinRows();
    this.allLoaded =
      this.departureCabinList.length === 0 ||
      this.departureCabinList.every((depData) => depData.initialized === true);
  }

  // Construye una fila por cada salida (opción aplanada). Se preservan los valores
  // de adults/children ya elegidos por el usuario si vuelve a llegar una respuesta
  // de findCabins (las peticiones se resuelven de forma asíncrona y desordenada).
  private rebuildCabinRows(): void {
    const previous = this.cabinRows.map((row) => ({
      optionIndex: Number(row.get("cabinaItinerarioMenor").value),
      adults: Number(row.get("adults").value) || 0,
      children: Number(row.get("children").value) || 0,
    }));
    this.cabinRows = this.allDepartureOptions.map((option, optionIndex) => {
      const row = this.createCabinForm();
      row.get("cabinaItinerarioMenor").setValue(optionIndex);
      // RF: acomodación del objeto cabin: primera cama/estado disponible
      // (igual que en el flujo individual; antes quedaba -1 en el request).
      const beds = option.wrapper ? option.wrapper.getCamaCabinaList() : null;
      row.get("camaCabinaList").setValue(
        beds && beds.length > 0 ? beds[0].code : -1
      );
      const prior = previous.find((item) => item.optionIndex === optionIndex);
      if (prior) {
        row.get("adults").setValue(prior.adults);
        row.get("children").setValue(prior.children);
      }
      return row;
    });
  }

  private buildOptionLabel(
    depData: DepartureCabinData,
    wrapper: CabinaItinerarioMenorWrapper
  ): string {
    // Ítem del select "Select Departure" (y fila de la tabla): [fecha, itinerario],
    // p. ej. [Sep 21, 2026, BARTOLOME-BAHIA SULLIVAN].
    const dateStr = this.formatDate(depData.departure.startDate);
    const name = depData.departure.name || "";
    return `[${dateStr}, ${name}]`;
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) {
      return "";
    }
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime())
        ? dateStr
        : d.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
    } catch (error) {
      return dateStr;
    }
  }

  private selectedOptionFromRow(row: FormGroup): FlatCabinOptionData {
    if (isNullOrUndefined(row)) {
      return null;
    }
    const index = Number(row.get("cabinaItinerarioMenor").value);
    if (isNullOrUndefined(index) || index < 0 || index >= this.allDepartureOptions.length) {
      return null;
    }
    return this.allDepartureOptions[index];
  }

  private getRowDepData(row: FormGroup): DepartureCabinData {
    const option = this.selectedOptionFromRow(row);
    return option ? this.departureCabinList[option.departureIndex] : null;
  }

  private getRowWrapper(row: FormGroup): CabinaItinerarioMenorWrapper {
    const option = this.selectedOptionFromRow(row);
    return option ? option.wrapper : null;
  }

  private getRowDepDataByIndex(rowIndex: number): DepartureCabinData {
    return this.getRowDepData(this.cabinRows[rowIndex]);
  }

  private getRowWrapperByIndex(rowIndex: number): CabinaItinerarioMenorWrapper {
    return this.getRowWrapper(this.cabinRows[rowIndex]);
  }

  public createCabinForm(): FormGroup {
    return new FormGroup(
      {
        cabinaItinerarioMenor: new FormControl(-1, [
          Validators.required,
          Validators.min(0),
        ]),
        camaCabinaList: new FormControl(-1),
        adults: new FormControl(0, [Validators.required, Validators.min(0)]),
        children: new FormControl(0, [Validators.required, Validators.min(0)]),
      },
      this.validateCabinForm.bind(this)
    );
  }

  public validateCabinForm(cabinFormGroup: FormGroup): {
    validationError?: boolean;
    childrenWithoutAdult?: boolean;
  } {
    try {
      const resultError: any = {};
      const cabinaItinerarioMenorValue = cabinFormGroup.get(
        "cabinaItinerarioMenor"
      ).value;
      if (isNullOrUndefined(cabinaItinerarioMenorValue) || cabinaItinerarioMenorValue < 0) {
        return { validationError: true };
      }
      const option = this.selectedOptionFromRow(cabinFormGroup);
      const wrapper = option ? option.wrapper : null;
      if (isNullOrUndefined(wrapper)) {
        return { validationError: true };
      }
      // Restaurado: no se permite un registro solo de niño (niño sin adulto).
      const adults = Number(cabinFormGroup.get("adults").value) || 0;
      const children = Number(cabinFormGroup.get("children").value) || 0;
      if (adults === 0 && children > 0) {
        resultError["childrenWithoutAdult"] = true;
      }
      return resultError;
    } catch (error) {
      console.error(error);
      return { validationError: true };
    }
  }

  private range(start: number, end: number): number[] {
    const values: number[] = [];
    for (let i = start; i <= end; i++) {
      values.push(i);
    }
    return values;
  }

  public adultsControl(index: number): FormControl {
    const row = this.cabinRows[index];
    return row ? (row.get("adults") as FormControl) : null;
  }

  public childrenControl(index: number): FormControl {
    const row = this.cabinRows[index];
    return row ? (row.get("children") as FormControl) : null;
  }

  public getRowLabel(rowIndex: number): string {
    const row = this.cabinRows[rowIndex];
    const option = this.selectedOptionFromRow(row);
    return option ? option.label : "";
  }

  public getRowAdults(rowIndex: number): number {
    const row = this.cabinRows[rowIndex];
    return row ? Number(row.get("adults").value) || 0 : 0;
  }

  public getRowChildren(rowIndex: number): number {
    const row = this.cabinRows[rowIndex];
    return row ? Number(row.get("children").value) || 0 : 0;
  }

  public getRowPassengers(rowIndex: number): number {
    return this.getRowAdults(rowIndex) + this.getRowChildren(rowIndex);
  }

  public getRowPrice(rowIndex: number): number {
    const wrapper = this.getRowWrapperByIndex(rowIndex);
    if (isNullOrUndefined(wrapper)) {
      return null;
    }
    return wrapper.getAppPrice(0, false, false, PassengerType.ADULT, this.cabinService);
  }

  // Capacidad de la salida de la fila = avail de esa salida (varía por salida).
  public getRowCapacity(rowIndex: number): number {
    const row = this.cabinRows[rowIndex];
    const option = this.selectedOptionFromRow(row);
    if (isNullOrUndefined(option)) {
      return 0;
    }
    const depData = this.departureCabinList[option.departureIndex];
    return depData && depData.departure
      ? Number(depData.departure.avail) || 0
      : 0;
  }

  // Opciones de Adults/Children por fila: 0..avail de esa salida.
  public getRowAdultsOptions(rowIndex: number): number[] {
    const capacity = this.getRowCapacity(rowIndex);
    return capacity > 0 ? this.range(0, capacity) : [0];
  }

  public getRowChildrenOptions(rowIndex: number): number[] {
    const capacity = this.getRowCapacity(rowIndex);
    return capacity > 0 ? this.range(0, capacity) : [0];
  }

  // La salida está activa (forma parte del bloqueo múltiple) cuando el usuario
  // cambió adults o children a un valor mayor que 0.
  public isRowActive(rowIndex: number): boolean {
    return this.getRowAdults(rowIndex) > 0 || this.getRowChildren(rowIndex) > 0;
  }

  public activeCount(): number {
    let count = 0;
    this.cabinRows.forEach((row, index) => {
      if (this.isRowActive(index)) {
        count++;
      }
    });
    return count;
  }

  // El basurero limpia adults/children de la fila (vuelve a 0/0) y la desactiva.
  public resetRowPassengers(rowIndex: number): void {
    const row = this.cabinRows[rowIndex];
    if (isNullOrUndefined(row)) {
      return;
    }
    row.get("adults").setValue(0);
    row.get("children").setValue(0);
  }

  public rowExceedsCapacity(rowIndex: number): boolean {
    const capacity = this.getRowCapacity(rowIndex);
    if (capacity <= 0) {
      return false;
    }
    return this.getRowPassengers(rowIndex) > capacity;
  }

  public getRowCapacityMessage(rowIndex: number): string {
    return (
      "This departure only accepts " +
      this.getRowCapacity(rowIndex) +
      " passengers but you have selected " +
      this.getRowPassengers(rowIndex) +
      "."
    );
  }

  public rowHasChildrenWithoutAdult(rowIndex: number): boolean {
    const row = this.cabinRows[rowIndex];
    return row ? row.hasError("childrenWithoutAdult") : false;
  }

  public hasChildrenWithoutAdult(): boolean {
    return this.cabinRows.some((row, index) => this.rowHasChildrenWithoutAdult(index));
  }

  public hasExceededDepartures(): boolean {
    return this.cabinRows.some((row, index) => this.rowExceedsCapacity(index));
  }

  // Para enviar se requieren al menos 2 salidas activas y sin validaciones fallidas.
  public canSubmitSelection(): boolean {
    return (
      this.activeCount() >= 2 &&
      !this.hasExceededDepartures() &&
      !this.hasChildrenWithoutAdult()
    );
  }

  private getDepartureTotalPassengers(departureIndex: number): number {
    let adults = 0;
    let children = 0;
    this.cabinRows.forEach((row) => {
      const option = this.selectedOptionFromRow(row);
      if (option && option.departureIndex === departureIndex) {
        adults += Number(row.get("adults").value) || 0;
        children += Number(row.get("children").value) || 0;
      }
    });
    return adults + children;
  }

  public get emailValue(): string {
    return this.formGroupOnHold.get("email").value;
  }
  public get referenceValue(): string {
    return this.formGroupOnHold.get("reference").value;
  }
  public get remarksValue(): string {
    return this.formGroupOnHold.get("remarks").value;
  }
  public get agencyValue(): string {
    return this.formGroupOnHold.get("agency").value;
  }
  public get itiObjectValue(): any {
    return this.formGroupOnHold.get("itiObject").value;
  }

  public getValid(controlName: string): boolean {
    return (
      this.formGroupOnHold.get(controlName).invalid &&
      this.formGroupOnHold.get(controlName).touched
    );
  }

  public getMainColor(): string {
    return "#" + this.dataCompany.mainColor;
  }

  public getSecundaryColor(): string {
    return "#" + this.dataCompany.secondaryColor;
  }

  public isDayTour(): boolean {
    return this.dataCompany ? this.dataCompany.isDayTour === true : false;
  }

  save() {
    // El formulario de contacto y la selección de salidas deben ser válidos
    // (mínimo 2 salidas activas, sin exceso de capacidad ni niños sin adulto).
    if (this.formGroupOnHold.invalid || !this.canSubmitSelection()) {
      return;
    }
    const request = {
      email: this.emailValue,
      timeMaximumCode: this.dataCompany.timeMaximumCode,
      maximumOnHold: this.dataCompany.maximumOnHold,
      company: this.dataCompany.code,
    };
    try {
      this.isLoading = true;
      this.ngWebflowService.addWebFlowAction(
        WebFlowAction.ON_HOLD_REQUEST_CODE_START,
        { request },
        true
      );
      this.galavailServiceEcommer.getCodeVerification(request).subscribe((resp) => {
        this.hasEmailRequest = true;
        this.token = resp.token;
        this.messageCode = resp.message;
        this.codeError = resp.code;
        this.isLoading = false;
      });
    } catch (error) {
      this.isLoading = false;
      console.error(error);
    }
  }

  setCodeEntered(event) {
    this.codeValidationEntered = event;
  }

  public blockCabin(): void {
    if (!this.canSubmitSelection()) {
      return;
    }
    try {
      this.isLoadingOnHold = true;
      const valueRequest = this.createRequest();
      this.ngWebflowService.addWebFlowAction(
        WebFlowAction.ON_HOLD_REQUEST_START,
        valueRequest
      );
      this.onholdService.onHoldMultiple(valueRequest).subscribe(
        (resp) => {
          if (resp.status) {
            this.saveMultiResult();
            this.redirectThanksPage();
          } else {
            this.dialogObj = { title: "Error", message: resp.message };
            this.isVisibleDialog = true;
          }
          this.isLoadingOnHold = false;
        },
        (error) => {
          this.isLoadingOnHold = false;
          console.error(error);
        }
      );
    } catch (error) {
      this.isLoadingOnHold = false;
      console.error(error);
    }
  }

  private createRequest() {
    // RF: se conserva el body original del web service onHold (top-level `cabins`,
    // sin `multipleDepartures`); solo se agrega el campo `itinerary` a cada objeto
    // de `cabins`. La estructura `multipleDepartures` generaba error 500.
    const base = this.departureCabinList[0]
      ? this.departureCabinList[0].departure
      : null;
    // Solo se envían las salidas activas (adults > 0 o children > 0).
    const cabins = this.cabinRows
      .filter((row, index) => this.isRowActive(index))
      .map((row) => {
        const option = this.selectedOptionFromRow(row);
        if (isNullOrUndefined(option)) {
          return null;
        }
        const depData = this.departureCabinList[option.departureIndex];
        return {
          departure: option.wrapper.getCode(),
          adults: Number(row.get("adults").value) || 0,
          children: Number(row.get("children").value) || 0,
          acomodation: row.get("camaCabinaList").value,
          itinerary: depData ? depData.departure.code : option.wrapper.getCode(),
        };
      })
      .filter((cabin) => cabin !== null);

    return {
      token: this.token,
      validatorCode: Number(this.codeValidationEntered),
      email: this.emailValue,
      emails: [this.dataCompany.emailEcommerce],
      type: 1,
      reference: this.referenceValue,
      itinerary: base ? base.code : 0,
      boat:
        base && base.boat && !isNullOrUndefined(base.boat.code)
          ? base.boat.code.toString()
          : "",
      cabins,
      remarks:
        "Remarks: " +
        this.remarksValue +
        " Email: " +
        this.emailValue +
        " Agency: " +
        this.agencyValue,
      aditionalServices: base ? base.aditionalServices : [],
      itiObject: this.itiObjectValue,
      includesAirTickets: base ? base.includesAirTickets : false,
    };
  }

  public redirectThanksPage(): void {
    this.router.navigate([this.empresa + "/" + this.keyCompany + "/thanks-onhold"]);
  }

  private saveMultiResult(): void {
    // Solo se guardan las salidas activas (con pasajeros) para el resumen final.
    const multi = this.departureCabinList
      .map((depData, departureIndex) => {
        let adults = 0;
        let children = 0;
        this.cabinRows.forEach((row) => {
          const option = this.selectedOptionFromRow(row);
          if (option && option.departureIndex === departureIndex) {
            adults += Number(row.get("adults").value) || 0;
            children += Number(row.get("children").value) || 0;
          }
        });
        if (adults === 0 && children === 0) {
          return null;
        }
        return {
          code: depData.departure.code,
          startDate: depData.departure.startDate,
          endDate: depData.departure.endDate,
          name: depData.departure.name,
          boat: depData.departure.boat.name,
          adults,
          children,
        };
      })
      .filter((departure) => departure !== null);
    this.galavailServiceEcommer.setLocalStorage(
      "dataInquireMultiResult-" + this.keyCompany,
      multi
    );
  }

  public goBack(): void {
    this.router.navigate([this.empresa]);
  }
}
